import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TaxRegionKey } from '@capintel/instruments-lib';

import { updateComparisonTaxSettings } from '@/redux/modules/comparison';
import { ReduxState } from '@/redux/modules/types';
import { PartialCaComparisonTaxSettings } from '@/types/tax-analysis';
import { CA_DEFAULT_MARGINAL_TAX_RATE } from '../../constants';
import { Dispatch } from '@/redux';
import { FIELD_KEYS } from './constants';

const CA_FIELD_KEYS = FIELD_KEYS[TaxRegionKey.CA];

/** Default CA tax settings */
const DEFAULT_CA_SETTINGS: PartialCaComparisonTaxSettings = {
  marginalTaxRate: CA_DEFAULT_MARGINAL_TAX_RATE * 100,
};

export const useTaxAnalysisInputsCA = () => {
  const dispatch = useDispatch<Dispatch>();
  const activeComparison = useSelector((state: ReduxState) => state.comparison.activeComparison);
  const initialized = useRef(false);

  const taxSettings = activeComparison?.taxAnalysis?.taxSettings;
  const caSettings = taxSettings?.[TaxRegionKey.CA] ?? {};
  const defaultCaSettings: PartialCaComparisonTaxSettings = { ...DEFAULT_CA_SETTINGS, ...caSettings };

  // Initialize defaults in Redux once
  useEffect(() => {
    if (!initialized.current && activeComparison) {
      dispatch(updateComparisonTaxSettings({ region: TaxRegionKey.CA, [TaxRegionKey.CA]: defaultCaSettings }));
      initialized.current = true;
    }
  }, [activeComparison, dispatch, defaultCaSettings]);

  // Create setters that update the CA-specific settings
  const createCaSetter = useCallback(
    (key: keyof PartialCaComparisonTaxSettings) => (value?: number) => {
      if (!activeComparison) return;
      const updatedSettings: PartialCaComparisonTaxSettings = { ...caSettings, [key]: value };
      dispatch(updateComparisonTaxSettings({ region: TaxRegionKey.CA, [TaxRegionKey.CA]: updatedSettings }));
    },
    [activeComparison, dispatch, caSettings],
  );

  return {
    values: caSettings,
    setYTDGain: createCaSetter(CA_FIELD_KEYS.YTD_GAIN),
    setYTDLoss: createCaSetter(CA_FIELD_KEYS.YTD_LOSS),
    setCarryForwardLoss: createCaSetter(CA_FIELD_KEYS.CARRY_FORWARD_LOSS),
    setMarginalTaxRate: createCaSetter(CA_FIELD_KEYS.MARGINAL_TAX_RATE),
  };
};
